import React from 'react';
import { Field, FieldProps } from 'formik';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { Field as SitecoreField } from '@sitecore-content-sdk/nextjs';

const radioGroupStyles = tv({
  base: ['flex', 'flex-col', 'gap-spacing-spacing-24'],
});

const radioOptionStyles = tv({
  base: ['flex', 'items-center', 'gap-spacing-spacing-12', 'cursor-pointer', 'group'],
  variants: {
    state: {
      default: '',
      error: '',
      disabled: ['opacity-40', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const radioInputWrapperStyles = tv({
  base: [
    'w-7',
    'h-7',
    'border',
    'border-color-input-border',
    'rounded-full',
    'bg-color-grayscale-white',
    'cursor-pointer',
    'flex-shrink-0',
    'flex',
    'items-center',
    'justify-center',
    'transition-all',
    'duration-200',
    'hover:border-color-input-border-hover',
    'relative',
  ],
  variants: {
    state: {
      default: '',
      error: [
        'border-color-text-text-critical',
        'focus-within:border-color-text-text-error',
        'focus-within:ring-color-text-text-error',
      ],
      disabled: ['bg-color-grayscale-gray-100', 'cursor-not-allowed', 'opacity-40'],
    },
    checked: {
      true: ['border-color-input-border'],
      false: '',
    },
  },
  defaultVariants: {
    state: 'default',
    checked: false,
  },
});

const radioInputStyles = tv({
  base: ['sr-only'],
});

const radioDotStyles = tv({
  base: [
    'w-3.5',
    'h-3.5',
    'rounded-full',
    'bg-color-core-color-4-SkyBlue',
    'transition-opacity',
    'duration-200',
  ],
  variants: {
    checked: {
      true: ['opacity-100'],
      false: ['opacity-0'],
    },
    state: {
      default: '',
      error: ['bg-color-text-text-error'],
      disabled: '',
    },
  },
  defaultVariants: {
    checked: false,
    state: 'default',
  },
});

const radioLabelStyles = tv({
  base: [
    'text-color-core-color-1-Navy',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'leading-typography-line-height-body-medium',
    'font-normal',
    'cursor-pointer',
    'select-none',
  ],
  variants: {
    state: {
      default: '',
      error: 'text-color-text-text-critical',
      disabled: ['text-color-text-text-disabled', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

interface RadioOption {
  value: string;
  label: string;
}

interface RadioFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  hideLabel?: boolean;
  isStandalone?: boolean;
}

// Standalone radio component that works without Formik
const RadioFieldStandalone: React.FC<{
  fieldName: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  hasError?: boolean;
  'aria-required'?: boolean;
}> = ({
  fieldName,
  options,
  value = '',
  onChange,
  onBlur,
  isDisabled = false,
  hasError = false,
  'aria-required': ariaRequired = false,
}) => {
  const fieldState = isDisabled ? 'disabled' : hasError ? 'error' : 'default';

  const handleChange = (optionValue: string) => {
    if (onChange) {
      onChange(optionValue);
    }
  };

  return (
    <div className={radioGroupStyles()} role="radiogroup" aria-required={ariaRequired}>
      {options.map((option) => {
        const optionId = `${fieldName}-${option.value}`;
        const isChecked = value === option.value;

        return (
          <label
            key={option.value}
            htmlFor={optionId}
            className={radioOptionStyles({ state: fieldState })}
          >
            <div
              className={radioInputWrapperStyles({
                state: fieldState,
                checked: isChecked,
              })}
            >
              <input
                type="radio"
                name={fieldName}
                id={optionId}
                value={option.value}
                checked={isChecked}
                onChange={() => handleChange(option.value)}
                onBlur={onBlur}
                disabled={isDisabled}
                className={radioInputStyles()}
                aria-describedby={hasError ? `${fieldName}-error` : undefined}
                data-automation={`radioField-${fieldName}-${option.value}`}
              />
              <div
                className={radioDotStyles({
                  checked: isChecked,
                  state: fieldState,
                })}
                aria-hidden="true"
              />
            </div>
            <span className={radioLabelStyles({ state: fieldState })}>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};

// Custom Formik Field component for radio buttons
const CustomRadioField: React.FC<
  FieldProps & {
    options: RadioOption[];
    isDisabled?: boolean;
    hasError?: boolean;
    'aria-required'?: boolean;
    fieldName: string;
  }
> = ({
  field,
  form,
  options,
  isDisabled = false,
  hasError = false,
  'aria-required': ariaRequired = false,
  fieldName,
}) => {
  const fieldState = isDisabled ? 'disabled' : hasError ? 'error' : 'default';

  const handleChange = async (value: string) => {
    try {
      await form.setFieldValue(field.name, value, false);
      await form.setFieldTouched(field.name, true, true);
    } catch (error) {
      console.error('Error updating radio field:', field.name, error);
      form.setFieldValue(field.name, value, false);
    }
  };

  const handleBlur = () => {
    form.setFieldTouched(field.name, true, true);
  };

  return (
    <div className={radioGroupStyles()} role="radiogroup" aria-required={ariaRequired}>
      {options.map((option) => {
        const optionId = `${fieldName}-${option.value}`;
        const isChecked = field.value === option.value;

        return (
          <label
            key={option.value}
            htmlFor={optionId}
            className={radioOptionStyles({ state: fieldState })}
          >
            <div
              className={radioInputWrapperStyles({
                state: fieldState,
                checked: isChecked,
              })}
            >
              <Field
                type="radio"
                name={field.name}
                id={optionId}
                value={option.value}
                checked={isChecked}
                onChange={() => handleChange(option.value)}
                onBlur={handleBlur}
                disabled={isDisabled}
                className={radioInputStyles()}
                aria-required={ariaRequired}
                aria-describedby={hasError ? `${fieldName}-error` : undefined}
                data-automation={`radioField-${fieldName}-${option.value}`}
              />
              <div
                className={radioDotStyles({
                  checked: isChecked,
                  state: fieldState,
                })}
                aria-hidden="true"
              />
            </div>
            <span className={radioLabelStyles({ state: fieldState })}>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};

export const RadioField: React.FC<RadioFieldProps> = ({
  field,
  hasError = false,
  errors,
  touched,
  value,
  onChange,
  onBlur,
  hideLabel,
  isStandalone = false,
}) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;

  if (!fieldName) return null;

  // Transform Sitecore options to radio format
  const options: RadioOption[] = (fieldConfig?.selectOptionsAndSection || []).map((option) => ({
    value: (option.fields?.value as SitecoreField<string>)?.value || '',
    label: (option.fields?.text as SitecoreField<string>)?.value || '',
  }));

  if (options.length === 0) return null;

  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
      errors={errors}
      touched={touched}
      hideLabel={hideLabel}
    >
      {isStandalone ? (
        <RadioFieldStandalone
          fieldName={fieldName}
          options={options}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isDisabled={disabled}
          hasError={hasError}
          aria-required={required}
        />
      ) : (
        <Field
          name={fieldName}
          component={CustomRadioField}
          options={options}
          isDisabled={disabled}
          hasError={hasError}
          aria-required={required}
          fieldName={fieldName}
        />
      )}
    </FormFieldWrapper>
  );
};

export default RadioField;
